import React, { useState } from 'react';
import { Lock, ExternalLink, Copy, Trash2, Plus, Pencil, X } from 'lucide-react';
import { PageLayout, PageHeader, PageCard } from '../PageLayout';
import { useTheme } from '../../contexts/ThemeContext';

export type PasswordEntry = {
  id: string;
  name: string;
  uNummer?: string;
  passwort?: string;
  link?: string;
  info?: string;
  createdAt?: string;
};

type PasswordFormData = {
  id?: string;
  name: string;
  uNummer: string;
  passwort: string;
  link: string;
  info: string;
};

type Props = {
  passwords: PasswordEntry[];
  currentUser: any;
  onSavePassword: (data: PasswordFormData) => Promise<void>;
  onDeletePassword: (id: string) => void;
};

const emptyForm: PasswordFormData = {
  name: '',
  uNummer: '',
  passwort: '',
  link: '',
  info: '',
};

const PasswordsView: React.FC<Props> = ({ passwords, currentUser, onSavePassword, onDeletePassword }) => {
  const { theme } = useTheme();
  const isDark =
    theme === 'dark' ||
    (theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showPasswordId, setShowPasswordId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<PasswordFormData | null>(null);
  const [saving, setSaving] = useState(false);

  const hasEntries = Array.isArray(passwords) && passwords.length > 0;

  const handleCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      // ignore
    }
  };

  const isAdmin = currentUser?.role === 'admin';

  const openNew = () => setEditForm({ ...emptyForm });
  const openEdit = (p: PasswordEntry) =>
    setEditForm({
      id: p.id,
      name: p.name || '',
      uNummer: p.uNummer || '',
      passwort: p.passwort || '',
      link: p.link || '',
      info: p.info || '',
    });
  const closeModal = () => setEditForm(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm || !editForm.name.trim()) return;
    setSaving(true);
    try {
      await onSavePassword(editForm);
      closeModal();
    } finally {
      setSaving(false);
    }
  };

  const inputClass = `w-full px-3 py-2 rounded-xl border text-sm outline-none focus:ring-2 focus:ring-cyan-500/50 ${
    isDark ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
  }`;
  const labelClass = `block text-xs font-semibold uppercase tracking-wider mb-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`;

  return (
    <PageLayout>
      <PageHeader
        title="Passwörter"
        subtitle="Zugänge zu Portalen und Systemen der Agentur"
        actions={
          isAdmin ? (
            <button
              type="button"
              onClick={openNew}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-colors ${
                isDark ? 'bg-cyan-500/30 text-cyan-200 border border-cyan-400/50 hover:bg-cyan-500/40' : 'bg-cyan-100 text-cyan-800 border border-cyan-300 hover:bg-cyan-200'
              }`}
            >
              <Plus size={18} />
              Neues Passwort
            </button>
          ) : undefined
        }
      />

      <PageCard className="p-0 overflow-hidden">
        {!hasEntries ? (
          <div
            className={`p-10 text-center ${
              isDark ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            <Lock size={40} className="mx-auto mb-4 opacity-70" />
            <p className="font-semibold">Noch keine Passwörter hinterlegt.</p>
            <p className="text-sm mt-1">
              Importiere eine CSV-Datei oder lege Passwörter direkt in Firestore unter
              &nbsp;
              <code className="px-1 py-0.5 rounded bg-black/5">
                passwords
              </code>
              &nbsp;an.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead
                className={
                  isDark
                    ? 'bg-slate-800/80 border-b border-slate-700 text-slate-300'
                    : 'bg-slate-50 border-b border-slate-200 text-slate-600'
                }
              >
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Name / Portal</th>
                  <th className="px-4 py-3 text-left font-semibold">Benutzer</th>
                  <th className="px-4 py-3 text-left font-semibold">Passwort</th>
                  <th className="px-4 py-3 text-left font-semibold">Link</th>
                  <th className="px-4 py-3 text-right font-semibold w-20">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody
                className={
                  isDark ? 'divide-y divide-slate-700' : 'divide-y divide-slate-100'
                }
              >
                {passwords
                  .slice()
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((p) => {
                    const isShown = showPasswordId === p.id;
                    const pwd = p.passwort || '';
                    const masked =
                      pwd && !isShown ? '•'.repeat(Math.min(pwd.length, 12)) : pwd || '–';
                    const canEdit = isAdmin;

                    return (
                      <tr key={p.id} className={isDark ? 'hover:bg-slate-800/60' : 'hover:bg-slate-50'}>
                        <td className="px-4 py-3 align-top">
                          <div
                            className={`font-semibold ${
                              isDark ? 'text-white' : 'text-slate-900'
                            }`}
                          >
                            {p.name || 'Ohne Titel'}
                          </div>
                          {p.info && (
                            <div
                              className={`text-xs mt-1 ${
                                isDark ? 'text-slate-400' : 'text-slate-500'
                              }`}
                            >
                              {p.info}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <span
                            className={`font-mono text-xs ${
                              isDark ? 'text-slate-200' : 'text-slate-700'
                            }`}
                          >
                            {p.uNummer || '–'}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-center gap-2">
                            <span
                              className={`font-mono text-xs ${
                                isDark ? 'text-slate-100' : 'text-slate-900'
                              }`}
                            >
                              {masked}
                            </span>
                            {pwd && (
                              <button
                                type="button"
                                onClick={() =>
                                  setShowPasswordId(isShown ? null : p.id)
                                }
                                className={`text-xs underline ${
                                  isDark
                                    ? 'text-slate-400 hover:text-slate-200'
                                    : 'text-slate-500 hover:text-slate-800'
                                }`}
                              >
                                {isShown ? 'verbergen' : 'anzeigen'}
                              </button>
                            )}
                            {pwd && (
                              <button
                                type="button"
                                onClick={() => handleCopy(pwd, p.id)}
                                className={
                                  isDark
                                    ? 'text-slate-400 hover:text-slate-100'
                                    : 'text-slate-500 hover:text-slate-900'
                                }
                                title="Passwort kopieren"
                              >
                                {copiedId === p.id ? (
                                  <span className="text-[10px] font-semibold">
                                    Kopiert
                                  </span>
                                ) : (
                                  <Copy size={14} />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          {p.link ? (
                            <a
                              href={p.link}
                              target="_blank"
                              rel="noreferrer"
                              className={`inline-flex items-center gap-1 text-xs underline ${
                                isDark
                                  ? 'text-cyan-300 hover:text-cyan-200'
                                  : 'text-cyan-700 hover:text-cyan-900'
                              }`}
                            >
                              <ExternalLink size={12} />
                              Öffnen
                            </a>
                          ) : (
                            <span
                              className={
                                isDark ? 'text-slate-500 text-xs' : 'text-slate-400 text-xs'
                              }
                            >
                              –
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canEdit && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => openEdit(p)}
                                  className={
                                    isDark
                                      ? 'text-slate-400 hover:text-cyan-300'
                                      : 'text-slate-500 hover:text-cyan-600'
                                  }
                                  title="Bearbeiten"
                                >
                                  <Pencil size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => onDeletePassword(p.id)}
                                  className={
                                    isDark
                                      ? 'text-red-400 hover:text-red-300'
                                      : 'text-red-500 hover:text-red-700'
                                  }
                                  title="Löschen"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </PageCard>

      {/* Modal: Neues Passwort / Bearbeiten */}
      {editForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div
            className={`rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto border ${
              isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {editForm.id ? 'Passwort bearbeiten' : 'Neues Passwort'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className={labelClass}>Name / Portal *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => (f ? { ...f, name: e.target.value } : f))}
                  className={inputClass}
                  placeholder="z.B. Signal Iduna Portal"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Benutzer / Kennung</label>
                <input
                  type="text"
                  value={editForm.uNummer}
                  onChange={(e) => setEditForm((f) => (f ? { ...f, uNummer: e.target.value } : f))}
                  className={inputClass}
                  placeholder="Benutzername oder Nummer"
                />
              </div>
              <div>
                <label className={labelClass}>Passwort</label>
                <input
                  type="password"
                  value={editForm.passwort}
                  onChange={(e) => setEditForm((f) => (f ? { ...f, passwort: e.target.value } : f))}
                  className={inputClass}
                  placeholder="••••••••"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className={labelClass}>Link (URL)</label>
                <input
                  type="url"
                  value={editForm.link}
                  onChange={(e) => setEditForm((f) => (f ? { ...f, link: e.target.value } : f))}
                  className={inputClass}
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className={labelClass}>Info / Notiz</label>
                <input
                  type="text"
                  value={editForm.info}
                  onChange={(e) => setEditForm((f) => (f ? { ...f, info: e.target.value } : f))}
                  className={inputClass}
                  placeholder="Optionale Hinweise"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className={`flex-1 py-2.5 rounded-xl font-semibold text-sm ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={saving || !editForm.name.trim()}
                  className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-cyan-500 text-white hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Speichern…' : 'Speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default PasswordsView;

